// UX116 (docs/build-briefs/UX116-function-hubs.md): the four function hubs (Reports, Inventory, Ordering, Shift
// and closeout) as DATA. Pure and DOM free, so a plain node:test file can import it
// (test/function-hubs-model.test.ts) and ui/src/functionHubs.ts draws from it.
//
// Every figure comes from a read that already exists; this module only shapes it. A value the session may not
// read, or one the read could not return, is the word N/A and the tile is not tappable. Nothing here decides
// access: the caller passes `undefined` for a read it did not make because the person lacks the permission.

import type { MenuGroupId } from "./accountMenu.ts";
import { isAtPar } from "./inventoryListSort.ts";
import {
  cutoffText,
  formatMoney,
} from "./reportFormat.ts";

/** RPTLAYOUT1-F1-S2: Reports is no longer a hub (it opens the Reports home), so a hub is any other account menu group. */
export type HubId = Exclude<MenuGroupId, "reports">;

export const HUB_IDS: readonly HubId[] = ["inventory", "ordering", "shift"];

export const HUB_TITLES: Readonly<Record<HubId, string>> = {
  inventory: "Inventory",
  ordering: "Ordering",
  shift: "Shift and closeout",
};

export function isHubId(value: string | undefined): value is HubId {
  return value !== undefined && (HUB_IDS as readonly string[]).includes(value);
}

/** What an empty or unreadable value reads as, everywhere on a hub. */
export const NOT_AVAILABLE = "-";

// ---------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------

export type TileTone = "plain" | "warn" | "amber";

export interface HubDelta {
  readonly text: string;
  readonly direction: "up" | "down" | "flat";
}

export interface HubTile {
  readonly id: string;
  readonly label: string;
  /** The big figure, or N/A. */
  readonly value: string;
  /** The small line under it. Empty when there is none. */
  readonly sub: string;
  readonly tone: TileTone;
  readonly delta?: HubDelta;
  /** False for N/A: the tile shows the word and does not open anything. */
  readonly available: boolean;
}

export interface HubListRow {
  readonly name: string;
  readonly pill?: { readonly text: string; readonly tone: "red" | "amber" };
  readonly meta?: string;
  readonly right: string;
}

export interface HubList {
  readonly id: string;
  readonly label: string;
  readonly rows: readonly HubListRow[];
  readonly available: boolean;
  /** LOCFLOOR1 (sweep B5): the text of a "View all N" line under the rows, present only when the list is cut short. */
  readonly viewAll?: string;
}

export function naTile(id: string, label: string): HubTile {
  return { id, label, value: NOT_AVAILABLE, sub: "", tone: "plain", available: false };
}

export function naList(id: string, label: string): HubList {
  return { id, label, rows: [], available: false };
}

function tile(
  id: string,
  label: string,
  value: string,
  sub: string,
  tone: TileTone = "plain",
  delta?: HubDelta,
): HubTile {
  return {
    id,
    label,
    value,
    sub,
    tone,
    available: true,
    ...(delta === undefined ? {} : { delta }),
  };
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

// ---------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------

export interface StockValueInput {
  readonly totals: {
    readonly valueCents: number;
    readonly countedItems: number;
    /** Every item the report lists, counted or not (LOCFLOOR1, sweep B5: "44 of 47 counted"). */
    readonly items: number;
    /** MGRDAYMAJ1 (manager-day-12): items nobody has given starting stock yet. Listed in `items`, left out of `countedItems`. */
    readonly noStartingStock?: number;
  };
  readonly items: ReadonlyArray<{
    readonly name: string;
    readonly state: string;
    readonly belowPar: boolean;
    readonly belowZero: boolean;
    /** MGRDAYMAJ1 (manager-day-12): its zero is "not entered yet", so it is neither Out nor Low. */
    readonly noStartingStock?: boolean;
    readonly onHand: string | null;
    readonly onHandUnit: string | null;
    readonly par: string | null;
    /** The unit par is in; null when the report did not resolve one. */
    readonly parUnit?: string | null;
  }>;
}

/** "1.200" is "1.2", "3.00" is "3". */
export function trimQuantity(raw: string): string {
  if (!raw.includes(".")) return raw;
  return raw.replace(/0+$/, "").replace(/\.$/, "");
}

const COUNTED_UNITS = new Set(["bottle", "can", "keg", "case"]);

function quantityWithUnit(onHand: string | null, unit: string | null): string {
  if (onHand === null) return NOT_AVAILABLE;
  const trimmed = trimQuantity(onHand);
  if (unit === null || unit === "") return trimmed;
  const word = COUNTED_UNITS.has(unit) && trimmed !== "1" ? `${unit}s` : unit;
  return `${trimmed} ${word}`;
}

/** LOCFLOOR1 (sweep B4): exactly at par. The server's belowPar is inclusive (DEC-176), so an item sitting at its par
 * is in that list with nothing to order; it is not Low. Compared only when on hand and par are in the same unit. */
function sitsAtPar(item: StockValueInput["items"][number]): boolean {
  if (item.onHand === null || item.par === null) return false;
  const comparable =
    item.parUnit === undefined || item.parUnit === null || item.parUnit === item.onHandUnit;
  return comparable && isAtPar(Number(item.onHand), Number(item.par));
}

/** Items below par that still have stock: an item at zero is Out, and is counted there, not here; one exactly at par is
 * not low. The same rule draws the Low rows of Needs attention, so the tile and the list agree. */
function isLow(item: StockValueInput["items"][number]): boolean {
  return item.belowPar && item.state !== "out" && !item.belowZero && !sitsAtPar(item) && item.noStartingStock !== true;
}

export function lowTile(report: StockValueInput | undefined): HubTile {
  if (report === undefined) return naTile("low", "Low");
  const count = report.items.filter(isLow).length;
  return tile("low", "Low", String(count), "below par", count > 0 ? "amber" : "plain");
}

/** Items at zero. An item below zero has its own tag in the report and is not counted as out. */
export function outCount(report: StockValueInput): number {
  return report.items.filter((item) => item.state === "out" && !item.belowZero && item.noStartingStock !== true).length;
}

export function outTile(report: StockValueInput | undefined): HubTile {
  if (report === undefined) return naTile("out", "Out");
  const count = outCount(report);
  return tile("out", "Out", String(count), "on hand is zero", count > 0 ? "warn" : "plain");
}

export interface StockFlagInput {
  readonly liquorProductId: string;
  readonly flagged: boolean;
}

/** Products whose latest flag is on. The read is latest-only, but a product that appears twice counts once. */
export function flaggedTile(flags: readonly StockFlagInput[] | undefined): HubTile {
  if (flags === undefined) return naTile("flagged", "86'd");
  const on = new Set(flags.filter((flag) => flag.flagged).map((flag) => flag.liquorProductId));
  return tile(
    "flagged",
    "86'd",
    String(on.size),
    "off the menu now",
    on.size > 0 ? "warn" : "plain",
  );
}

export function stockValueTile(report: StockValueInput | undefined): HubTile {
  if (report === undefined) return naTile("stock-value", "Stock value");
  return tile(
    "stock-value",
    "Stock value",
    formatMoney(report.totals.valueCents),
    // LOCFLOOR1 (sweep B5): "44 of 47 counted", so a person sees how many items the value leaves out.
    `${report.totals.countedItems} of ${report.totals.items} counted`,
  );
}

const NEEDS_ATTENTION_ROWS = 4;

/** Out items first, then low ones, at most four with a "View all N" line when there are more, in the report's own order
 * within each. */
export function needsAttentionList(report: StockValueInput | undefined): HubList {
  if (report === undefined) return naList("needs-attention", "Needs attention");
  const out = report.items.filter((item) => item.state === "out" && !item.belowZero && item.noStartingStock !== true);
  const low = report.items.filter(isLow);
  const rows: HubListRow[] = [];
  for (const item of out) {
    rows.push({
      name: item.name,
      pill: { text: "Out", tone: "red" },
      right: quantityWithUnit(item.onHand ?? "0", item.onHandUnit),
    });
  }
  for (const item of low) {
    rows.push({
      name: item.name,
      pill: { text: "Low", tone: "amber" },
      right:
        item.onHand !== null && item.par !== null
          ? `${trimQuantity(item.onHand)} of ${trimQuantity(item.par)} par`
          : quantityWithUnit(item.onHand, item.onHandUnit),
    });
  }
  const shown = rows.slice(0, NEEDS_ATTENTION_ROWS);
  // MGRDAYMAJ1 (manager-day-12): items nobody has given starting stock are one neutral line that leads, not a red Out row each. It
  // is not one of the four items the list is capped to, so a location with stock to set up still sees its out and low items.
  const needStart = report.totals.noStartingStock ?? report.items.filter((item) => item.noStartingStock === true).length;
  const lead: HubListRow[] =
    needStart > 0
      ? [
          {
            name: `${needStart} ${needStart === 1 ? "item needs" : "items need"} starting stock`,
            right: "Set up",
          },
        ]
      : [];
  return {
    id: "needs-attention",
    label: "Needs attention",
    rows: [...lead, ...shown],
    available: true,
    ...(rows.length > shown.length ? { viewAll: `View all ${rows.length}` } : {}),
  };
}

export interface ClosedCountInput {
  readonly closed_at: string | null;
  readonly closed_by_display_name: string | null;
}

/** "Oct 9" from an ISO date (a calendar date) or an ISO timestamp (read in the device's own zone, like the
 * Past counts list). */
export function shortDate(value: string): string {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [, m, d] = value.split("-").map(Number) as [number, number, number];
    return `${months[m - 1]} ${d}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return NOT_AVAILABLE;
  return `${months[parsed.getMonth()]} ${parsed.getDate()}`;
}

/** The most recently closed count: its date and who closed it, nothing else. The closer is named from the read's
 * own closed_by_display_name (UX116-F1), and reads N/A when that does not resolve. */
export function lastCountTile(sessions: readonly ClosedCountInput[] | undefined): HubTile {
  if (sessions === undefined) return naTile("last-count", "Last count");
  const closed = sessions
    .filter((session) => session.closed_at !== null)
    .sort(
      (a, b) =>
        new Date(b.closed_at as string).getTime() - new Date(a.closed_at as string).getTime(),
    );
  const latest = closed[0];
  if (latest === undefined || latest.closed_at === null) {
    return { ...naTile("last-count", "Last count"), available: true };
  }
  const closer = latest.closed_by_display_name ?? NOT_AVAILABLE;
  return tile("last-count", "Last count", shortDate(latest.closed_at), `Closed by ${closer}`);
}

// ---------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------

export function cartTile(cartLines: number | undefined): HubTile {
  if (cartLines === undefined) return naTile("cart", "In cart");
  return tile("cart", "In cart", String(cartLines), "lines not placed");
}

export function approvalTile(count: number | undefined): HubTile {
  if (count === undefined) return naTile("approval", "Waiting approval");
  return tile(
    "approval",
    "Waiting approval",
    String(count),
    "needs a manager",
    count > 0 ? "amber" : "plain",
  );
}

export interface OrderInput {
  readonly status: string;
  readonly vendorName: string;
  readonly lineCount: number;
  readonly estimatedTotalCents: number;
}

/** `isReceivable` is purchasingDisplay.ts's purchaseOrderIsReceivable, passed in so this module imports no UI. */
export function toReceiveTile(
  orders: readonly OrderInput[] | undefined,
  isReceivable: (status: string) => boolean,
): HubTile {
  if (orders === undefined) return naTile("to-receive", "To receive");
  const count = orders.filter((order) => isReceivable(order.status)).length;
  return tile("to-receive", "To receive", String(count), "placed, not in yet");
}

export function toReceiveList(
  orders: readonly OrderInput[] | undefined,
  isReceivable: (status: string) => boolean,
): HubList {
  if (orders === undefined) return naList("to-receive-list", "Arriving soon");
  const receivable = orders.filter((order) => isReceivable(order.status));
  const rows = receivable.slice(0, 3).map((order) => ({
    name: order.vendorName,
    meta: plural(order.lineCount, "line", "lines"),
    right: formatMoney(order.estimatedTotalCents),
  }));
  // UIFIX5: the tile above counts every order on its way, so this list says how many it left out.
  const rest = receivable.slice(3);
  if (rest.length > 0) {
    rows.push({
      name: `${rest.length} more`,
      meta: "",
      right: formatMoney(rest.reduce((sum, order) => sum + order.estimatedTotalCents, 0)),
    });
  }
  // UIFIX5: the tile above is "To receive"; the list under it is the next few of those, so it has its own name.
  return { id: "to-receive-list", label: "Arriving soon", rows, available: true };
}

export function suggestedTile(count: number | undefined): HubTile {
  if (count === undefined) return naTile("suggested", "Suggested");
  return tile("suggested", "Suggested", String(count), "items below par");
}

// ---------------------------------------------------------------------
// Shift and closeout
// ---------------------------------------------------------------------

export interface CloseoutInput {
  readonly businessDate: string;
  readonly openTabsAtDayEnd: ReadonlyArray<{
    readonly name: string | null;
    readonly createdAt: string;
    readonly itemCount: number;
    /** What a Pay right now would charge, tax included (LOCFLOOR1, sweep B1): the same amount the Open tabs
     * screen shows for the ticket, never the pre-tax balance. */
    readonly owedWithTaxCents: number;
  }>;
  readonly netSales: { readonly ticketsPaid: number; readonly netSalesCents: number };
  readonly finalization: { readonly businessDate: string } | null;
  readonly finalizeAvailability: {
    readonly dayIsOver: boolean;
    readonly cutoffTimeLocal: string;
  } | null;
}

export function openTicketsTile(summary: CloseoutInput | undefined): HubTile {
  if (summary === undefined) return naTile("open-tickets", "Open tickets");
  const open = summary.openTabsAtDayEnd;
  const owed = open.reduce((sum, ticket) => sum + ticket.owedWithTaxCents, 0);
  return tile(
    "open-tickets",
    "Open tickets",
    String(open.length),
    open.length === 0 ? "" : `${formatMoney(owed)} still owed`,
    open.length > 0 ? "amber" : "plain",
  );
}

/** MGRDAYMAJ1 (manager-day-11): every open ticket at the location, from the Open Tabs list itself, whatever day it was opened on.
 * The closeout summary only lists tabs of ONE business day, so a hub read from it said 0 while tabs carried over from last night
 * still owed money. `owedWithTaxCents` is absent or null when a ticket could not be priced. */
export interface OpenTicketInput {
  readonly orderId: string;
  readonly name: string | null;
  readonly createdAt: string;
  readonly businessDate: string;
  readonly drinkCount: number;
  readonly owedWithTaxCents: number | null | undefined;
}

export function openTicketsTileFromTabs(tickets: readonly OpenTicketInput[], today: string): HubTile {
  const earlier = tickets.filter((ticket) => ticket.businessDate < today).length;
  const known = tickets.every((ticket) => typeof ticket.owedWithTaxCents === "number");
  const owed = tickets.reduce((sum, ticket) => sum + (ticket.owedWithTaxCents ?? 0), 0);
  const parts: string[] = [];
  if (tickets.length > 0 && known) parts.push(`${formatMoney(owed)} still owed`);
  if (earlier > 0) parts.push(`${earlier} from earlier days`);
  return tile(
    "open-tickets",
    "Open tickets",
    String(tickets.length),
    parts.join(", "),
    tickets.length > 0 ? "amber" : "plain",
  );
}

export function openTicketsListFromTabs(tickets: readonly OpenTicketInput[], nowMs: number): HubList {
  const rows = tickets.slice(0, 3).map((ticket) => {
    const age = ticketAge(ticket.createdAt, nowMs);
    const drinks = plural(ticket.drinkCount, "drink", "drinks");
    return {
      name: ticket.name ?? "Ticket",
      meta: age === "0 min" ? drinks : `${drinks}, ${age}`,
      right: typeof ticket.owedWithTaxCents === "number" ? formatMoney(ticket.owedWithTaxCents) : NOT_AVAILABLE,
    };
  });
  const rest = tickets.slice(3);
  if (rest.length > 0) {
    rows.push({
      name: `${rest.length} more`,
      meta: "",
      right: rest.every((ticket) => typeof ticket.owedWithTaxCents === "number")
        ? formatMoney(rest.reduce((sum, ticket) => sum + (ticket.owedWithTaxCents ?? 0), 0))
        : NOT_AVAILABLE,
    });
  }
  return { id: "open-tickets-list", label: "Open tickets", rows, available: true };
}

/** "22 min", "1 h 12 min", from the ticket's creation and a clock the caller supplies. */
export function ticketAge(createdAt: string, nowMs: number): string {
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return NOT_AVAILABLE;
  const minutes = Math.max(0, Math.floor((nowMs - created) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

export function openTicketsList(summary: CloseoutInput | undefined, nowMs: number): HubList {
  if (summary === undefined) return naList("open-tickets-list", "Open tickets");
  const all = summary.openTabsAtDayEnd;
  const rows = all.slice(0, 3).map((ticket) => {
    // NIGHTFIX1 (4): a ticket opened under a minute ago carries no age, as on the Open tabs card; never "0 min".
    const age = ticketAge(ticket.createdAt, nowMs);
    const drinks = plural(ticket.itemCount, "drink", "drinks");
    return {
      name: ticket.name ?? "Ticket",
      meta: age === "0 min" ? drinks : `${drinks}, ${age}`,
      right: formatMoney(ticket.owedWithTaxCents),
    };
  });
  // UIFIX5: the tile counts every open ticket, so a list that shows three says how many it left out.
  const rest = all.slice(3);
  if (rest.length > 0) {
    rows.push({
      name: `${rest.length} more`,
      meta: "",
      right: formatMoney(rest.reduce((sum, ticket) => sum + ticket.owedWithTaxCents, 0)),
    });
  }
  return { id: "open-tickets-list", label: "Open tickets", rows, available: true };
}

export function netSalesTodayTile(summary: CloseoutInput | undefined): HubTile {
  if (summary === undefined) return naTile("net-sales-today", "Net sales today");
  return tile(
    "net-sales-today",
    "Net sales today",
    formatMoney(summary.netSales.netSalesCents),
    `${plural(summary.netSales.ticketsPaid, "ticket", "tickets")} paid`,
  );
}

/** Where a current-shift tile would be: the system has no shift record, so this is when the business day closes. */
export function dayClosesTile(summary: CloseoutInput | undefined): HubTile {
  if (summary === undefined || summary.finalizeAvailability === null) {
    return naTile("day-closes", "Day closes");
  }
  const availability = summary.finalizeAvailability;
  return tile(
    "day-closes",
    "Day closes",
    cutoffText(availability.cutoffTimeLocal),
    availability.dayIsOver ? "Finalize is open" : "Finalize opens after",
  );
}

export function lastCloseoutTile(
  yesterday: CloseoutInput | undefined,
  yesterdayDate: string,
): HubTile {
  if (yesterday === undefined) return naTile("last-closeout", "Last closeout");
  const value = shortDate(yesterdayDate);
  if (yesterday.finalization === null)
    // MGRDAYMAJ1 (manager-day-05): a past day that had sales or open tabs and is still open is the job in front of the manager,
    // so it reads as one; a quiet day keeps the plain words.
    return yesterday.netSales.ticketsPaid > 0 || yesterday.openTabsAtDayEnd.length > 0
      ? tile("last-closeout", "Last closeout", value, "Needs closing", "amber")
      : tile("last-closeout", "Last closeout", value, "Not finalized");
  return tile(
    "last-closeout",
    "Last closeout",
    value,
    `Finalized, ${formatMoney(yesterday.netSales.netSalesCents)} net`,
  );
}
